import React, { useState } from "react";
import Balancer from "react-wrap-balancer";
import { TabList, TabPanel, TabPanels, Tabs, TabIndicator, Flex, Text, type SpaceProps } from "@chakra-ui/react";
import { AnimatePresence } from "framer-motion";
import { type Theme } from "contexts/color-theme";
import { ChakraBox } from "ui/chakra-framer";
import { remCalc } from "theme/utils";
import { FilterTab, type FilterTabProps } from "./filter-tab";

const fadeIn = {
  initial: { opacity: 0.5 },
  animate: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0.5, transition: { duration: 0.25 } },
};

export type FilterTabsProps = {
  /**
   * Determines whether or not the tab list renders a logo or text.
   *
   * @default "text"
   */
  variant?: "text" | "logo";

  /**
   * The color theme of the tabs.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The tabs to display.
   */
  tabs: FilterTabProps[];

  /**
   * The alignment of the tabs.
   *
   * @default "center"
   */
  justify?: "center" | "end";

  /**
   * Optional section title to display inline with the tabs
   * Should only be used when justify is set to `end`, otherwise just render `TextTitle` above the tabs
   */
  leftJustifiedTitle?: string;
} & SpaceProps;

export function FilterTabs({
  tabs,
  leftJustifiedTitle,
  justify = "center",
  theme = "light",
  variant = "text",
  ...props
}: FilterTabsProps) {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const tabHeight = variant === "text" ? remCalc(44) : { base: remCalc(44), md: "biggest" };

  return (
    <Tabs
      index={activeTabIndex}
      variant="unstyled"
      display="flex"
      flexDir="column"
      gap={{ base: "biggest", md: leftJustifiedTitle ? "huge" : "biggest" }}
      onChange={(tabIndex) => {
        setActiveTabIndex(tabIndex);
      }}
      {...props}
    >
      <Flex
        alignItems={{ base: "center", md: justify }}
        gap="big"
        justify={{ base: "center", md: leftJustifiedTitle ? "space-between" : justify }}
        w={leftJustifiedTitle ? "100%" : "auto"}
        wrap={{ base: "wrap", lg: "nowrap" }}
      >
        {leftJustifiedTitle && (
          <Text
            as="h2"
            color={`brand.text.${theme}`}
            textStyle={{ base: "headline1", md: "display3" }}
            textAlign={{ base: "center", md: "left" }}
          >
            <Balancer>{leftJustifiedTitle}</Balancer>
          </Text>
        )}

        <TabList
          bg={`brand.backgroundSecondary.${theme}`}
          borderRadius="small"
          display="flex"
          alignItems="center"
          gap={{ base: "tinier", md: variant === "text" ? "tiny" : "smallest" }}
          flexShrink={0}
          maxW="100%"
          overflowX="auto"
          p="tiny"
          pos="relative" // Required for the TabIndicator to resize properly
        >
          {tabs.map((tab, index) => (
            <FilterTab
              key={tab.label}
              isActive={index === activeTabIndex}
              height={tabHeight}
              label={tab.label}
              logo={tab.logo}
              theme={theme}
              items={tab.items}
            />
          ))}

          <TabIndicator bg={`brand.text.${theme}`} borderRadius="base" height={tabHeight} py="tiniest" />
        </TabList>
      </Flex>

      <TabPanels>
        {tabs.map((tab, index) => (
          <TabPanel key={tab.label} p={0}>
            <AnimatePresence>
              <ChakraBox
                key={`${tab.label}-content`}
                variants={fadeIn}
                initial="initial"
                animate={activeTabIndex === index ? "animate" : "exit"}
              >
                {tab.items}
              </ChakraBox>
            </AnimatePresence>
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
}
